import React, { useMemo, useState, useCallback } from "react";
import moment from "moment";
import Datatable from "../../../core/common/dataTable";
import BarChart from "./BarChart";
import StatCard from "../adminDashboard/components/StatsCard";

type Props = {
    employeeData: any;
    selectedCompany: any;
    isLoading: boolean;
};

const AttritionReport: React.FC<Props> = ({
    employeeData,
    selectedCompany,
    isLoading
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [viewType, setViewType] = useState<"monthly" | "department">("monthly");

    const monthlyAttritionData = useMemo(() => {
        if (!employeeData?.monthlyAttrition?.currentYear) return [];

        const rawData = employeeData.monthlyAttrition.currentYear;
        const processedData = rawData.map((month: any, index: number) => {
            const prevYearMonth = employeeData?.monthlyAttrition?.previousYear?.[index];
            const currentAttrition = parseFloat(month.attritionPercentage) || 0;
            const prevAttrition = prevYearMonth ? parseFloat(prevYearMonth.attritionPercentage) || 0 : 0;
            const trend = currentAttrition > prevAttrition ? 'up' : currentAttrition < prevAttrition ? 'down' : 'same';

            return {
                month: month.month || '',
                openingHC: parseInt(month.openingHC) || 0,
                newJoiners: parseInt(month.newJoiners) || 0,
                totalExits: parseInt(month.totalExits) || 0,
                closingHC: parseInt(month.closingHC) || 0,
                attritionPercentage: month.attritionPercentage || '0.00%',
                trend,
                prevYearMonth,
                yoyComparison: prevAttrition,
                changeVsPrev: currentAttrition - prevAttrition,
                timestamp: new Date(`01 ${month.month}`).getTime()
            };
        });

        return processedData.reverse();
    }, [employeeData]);

    const departmentAttritionData = useMemo(() => {
        if (!employeeData?.departmentStatistics?.departments) return [];

        return employeeData.departmentStatistics.departments.map((dept: any) => {
            const exitedEmployees = dept.suspendedEmployees || 0;
            const attritionRate = dept.totalEmployees > 0
                ? (exitedEmployees / dept.totalEmployees * 100).toFixed(1)
                : '0.0';

            return {
                departmentName: dept.departmentName,
                totalEmployees: dept.totalEmployees,
                activeEmployees: dept.activeEmployees,
                exitedEmployees,
                attritionRate: `${attritionRate}%`,
                attritionPercent: parseFloat(attritionRate),
                departmentId: dept.departmentId
            };
        }).sort((a: any, b: any) => b.attritionPercent - a.attritionPercent);
    }, [employeeData]);

    const displayData = useMemo(() => {
        return viewType === "monthly" ? monthlyAttritionData : departmentAttritionData;
    }, [viewType, monthlyAttritionData, departmentAttritionData]);

    const monthlyColumns = useMemo(() => [
        {
            title: 'Month',
            dataIndex: 'month',
            key: 'month',
            align: 'center',
            sorter: (a: any, b: any) => {
                const parseMonthYear = (monthStr: string) => {
                    const [monthAbbr, yearAbbr] = monthStr.split(' ');

                    const monthMap: { [key: string]: number } = {
                        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                    };

                    const fullYear = 2000 + parseInt(yearAbbr);
                    const month = monthMap[monthAbbr] || 0;

                    return new Date(fullYear, month, 1).getTime();
                };

                return parseMonthYear(a.month) - parseMonthYear(b.month);
            },
            render: (text: string) => {
                const [month, year] = text.split(' ');
                return <strong>{`${month} 20${year}`}</strong>;
            }
        },
        {
            title: 'Opening HC',
            dataIndex: 'openingHC',
            key: 'openingHC',
            align: 'center',
            sorter: (a: any, b: any) => a.openingHC - b.openingHC,
            render: (text: number) => <span className="text-muted">{text.toLocaleString()}</span>
        },
        {
            title: 'New Joiners',
            dataIndex: 'newJoiners',
            key: 'newJoiners',
            align: 'center',
            sorter: (a: any, b: any) => a.newJoiners - b.newJoiners,
            render: (text: number) => (
                <span className="badge bg-success-light text-success">+{text}</span>
            )
        },
        {
            title: 'Total Exits',
            dataIndex: 'totalExits',
            key: 'totalExits',
            align: 'center',
            sorter: (a: any, b: any) => a.totalExits - b.totalExits,
            render: (text: number) => (
                <span className="badge bg-danger-light text-danger">{text}</span>
            )
        },
        {
            title: 'Closing HC',
            dataIndex: 'closingHC',
            key: 'closingHC',
            align: 'center',
            sorter: (a: any, b: any) => a.closingHC - b.closingHC,
            render: (text: number) => <strong className="text-primary">{text.toLocaleString()}</strong>
        },
        {
            title: 'Attrition %',
            dataIndex: 'attritionPercentage',
            key: 'attritionPercentage',
            align: 'center',
            sorter: (a: any, b: any) => parseFloat(a.attritionPercentage) - parseFloat(b.attritionPercentage),
            render: (text: string, record: any) => {
                const trend = record.trend;
                return (
                    <div className="d-flex align-items-center gap-1 justify-content-center">
                        <span className={`fw-bold ${parseFloat(text) > 10 ? 'text-danger' : parseFloat(text) > 5 ? 'text-warning' : 'text-success'}`}>
                            {text}
                        </span>
                        {trend !== 'same' && (
                            <i className={`ti ti-trending-${trend === 'up' ? 'up text-danger' : 'down text-success'}`} />
                        )}
                    </div>
                );
            }
        },
        {
            title: 'YoY Comparison',
            dataIndex: 'yoyComparison',
            key: 'yoyComparison',
            align: 'center',
            sorter: (a: any, b: any) => a.yoyComparison - b.yoyComparison,
            render: (text: number) => (
                <span className="text-muted">{text ? `${text.toFixed(1)}%` : 'N/A'}</span>
            )
        },
        {
            title: 'Change',
            dataIndex: 'changeVsPrev',
            key: 'changeVsPrev',
            align: 'center',
            sorter: (a: any, b: any) => a.changeVsPrev - b.changeVsPrev,
            render: (text: number) => (
                <span className={`badge ${text > 0 ? 'bg-danger' : text < 0 ? 'bg-success' : 'bg-secondary'}`}>
                    {text > 0 ? '+' : ''}{text.toFixed(1)}%
                </span>
            )
        }
    ], []);


    const currentColumns = monthlyColumns;

    const exportToCSV = useCallback(() => {
        if (!displayData || displayData.length === 0) return;

        setIsExporting(true);

        try {
            const csvLines: string[] = [];

            csvLines.push(`"Attrition Report"`);
            csvLines.push(`"View Type","${viewType === "monthly" ? "Monthly Attrition" : "Department-wise Attrition"}"`);
            csvLines.push(`"Company","${selectedCompany?.name || "N/A"}"`);
            csvLines.push(`"Exported At","${moment().format("YYYY-MM-DD HH:mm:ss")}"`);
            csvLines.push("");

            if (viewType === "monthly") {
                csvLines.push(`"=== MONTHLY ATTRITION DETAILS ==="`);

                const headers = [
                    "Month",
                    "Opening HC",
                    "New Joiners",
                    "Total Exits",
                    "Closing HC",
                    "Attrition %",
                    "Previous Year %",
                    "Change (%)"
                ];

                csvLines.push(headers.map(h => `"${h}"`).join(","));

                displayData.forEach((row: any) => {
                    const [mon, yr] = row.month.split(" ");
                    const formattedMonth = `${mon} 20${yr}`;

                    csvLines.push([
                        formattedMonth,
                        row.openingHC,
                        row.newJoiners,
                        row.totalExits,
                        row.closingHC,
                        row.attritionPercentage,
                        row.yoyComparison ? `${row.yoyComparison.toFixed(1)}%` : "N/A",
                        `${row.changeVsPrev > 0 ? "+" : ""}${row.changeVsPrev.toFixed(1)}%`
                    ].map(v => `"${v}"`).join(","));
                });
            }

            if (viewType === "department") {
                csvLines.push(`"=== DEPARTMENT ATTRITION DETAILS ==="`);

                const headers = [
                    "Department",
                    "Total Employees",
                    "Active Employees",
                    "Exited Employees",
                    "Attrition Rate (%)"
                ];

                csvLines.push(headers.map(h => `"${h}"`).join(","));

                displayData.forEach((row: any) => {
                    csvLines.push([
                        row.departmentName,
                        row.totalEmployees,
                        row.activeEmployees,
                        row.exitedEmployees,
                        row.attritionRate
                    ].map(v => `"${v}"`).join(","));
                });
            }

            const csvContent = csvLines.join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Attrition_Report_${viewType}_${selectedCompany?.name?.replace(/\s+/g, "_") || "Company"}_${moment().format("YYYY-MM-DD_HH-mm")}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Attrition CSV Export Error:", error);
        } finally {
            setIsExporting(false);
        }
    }, [displayData, viewType, selectedCompany]);

    const summaryStats = useMemo(() => {
        if (!monthlyAttritionData.length) return null;

        const currentYearAvg = monthlyAttritionData.reduce((sum: number, month: any) =>
            sum + (parseFloat(month.attritionPercentage) || 0), 0) / monthlyAttritionData.length;

        const prevYearAvg = employeeData?.monthlyAttrition?.previousYear?.length > 0
            ? employeeData.monthlyAttrition.previousYear.reduce((sum: number, month: any) =>
                sum + (parseFloat(month.attritionPercentage) || 0), 0) / employeeData.monthlyAttrition.previousYear.length
            : 0;

        const highestMonth = [...monthlyAttritionData].sort((a: any, b: any) =>
            parseFloat(b.attritionPercentage) - parseFloat(a.attritionPercentage))[0];

        const totalExits = monthlyAttritionData.reduce((sum: number, month: any) =>
            sum + (month.totalExits || 0), 0);

        const yoyChange = currentYearAvg - prevYearAvg;

        return {
            currentYearAvg: currentYearAvg.toFixed(1) + '%',
            prevYearAvg: prevYearAvg.toFixed(1) + '%',
            yoyChange: yoyChange > 0 ? '+' + yoyChange.toFixed(1) + '%' : yoyChange.toFixed(1) + '%',
            highestMonth: highestMonth?.month || 'N/A',
            highestAttrition: highestMonth?.attritionPercentage || '0%',
            totalExits,
            rawYoyChange: yoyChange // For trend calculation
        };
    }, [monthlyAttritionData, employeeData]);
    return (
        <div className="">

            {/* Summary Stats */}
            {summaryStats && (
                <div className="card-body">
                    <div className="row">
                        <div className="col-lg-3 col-md-6 mb-3">
                            <StatCard
                                title="Current Year Avg."
                                value={parseFloat(summaryStats.currentYearAvg.replace('%', ''))}
                                change={summaryStats.yoyChange}
                                changeLabel="vs previous year"
                                trend={parseFloat(summaryStats.yoyChange.replace('%', '')) > 0 ? "up" : "down"}
                                icon="ti-chart-line"
                                description="Average monthly attrition rate for current year"
                                variant="primary"
                                valueFormat="percentage"
                            />
                        </div>
                        <div className="col-lg-3 col-md-6 mb-3">
                            <StatCard
                                title="Highest Month"
                                value={parseFloat(summaryStats.highestAttrition.replace('%', ''))}
                                change={`${summaryStats.highestMonth}`}
                                changeLabel="highest attrition month"
                                trend="up"
                                icon="ti-arrow-up"
                                description="Month with highest attrition rate"
                                variant="danger"
                                valueFormat="percentage"
                            />
                        </div>
                        <div className="col-lg-3 col-md-6 mb-3">
                            <StatCard
                                title="Total Exits"
                                value={summaryStats.totalExits}
                                change=""
                                changeLabel="current year total"
                                trend="down"
                                icon="ti-users-minus"
                                description="Total employee exits in current year"
                                variant="warning"
                                valueFormat="number"
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className="card mb-4">
                <div className="card-header d-flex align-items-center justify-content-between">
                    <div>
                        <h5 className="mb-0">Attrition Report</h5>
                        <small className="text-muted">Analyze employee attrition rates and trends</small>
                    </div>
                    <div className="d-flex align-items-center gap-3">

                        <button
                            className="btn btn-success"
                            onClick={exportToCSV}
                            disabled={isExporting || !displayData.length}
                        >
                            {isExporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-download me-2"></i>
                                    Export Report
                                </>
                            )}
                        </button>
                    </div>
                </div>

            </div>
            {/* Data Table */}
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">
                        {viewType === "monthly" ? "Monthly Attrition Details" : "Department Attrition Analysis"}
                        <small className="text-muted ms-2">({displayData.length} {viewType === "monthly" ? 'months' : 'departments'})</small>
                    </h5>
                </div>
                <div className="card-body p-0">
                    <Datatable
                        columns={currentColumns}
                        dataSource={displayData}
                        Selection={false}
                    />
                </div>
            </div>
            {/* Chart Section */}
            <div className="row mb-4">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">
                                {viewType === "monthly" ? "Monthly Attrition Trend" : "Department-wise Attrition"}
                            </h5>
                        </div>
                        <div className="card-body">
                            {displayData.length > 0 ? (
                                <div style={{ height: '300px' }}>
                                    <BarChart
                                        title=""
                                        dataPoints={(() => {
                                            if (viewType === "monthly") {
                                                const rawData = employeeData?.monthlyAttrition?.currentYear || [];
                                                const prevYearData = employeeData?.monthlyAttrition?.previousYear || [];

                                                return rawData.map((item: any, index: number) => {
                                                    const currentAttrition = parseFloat(item.attritionPercentage) || 0;
                                                    const prevAttrition = prevYearData[index] ?
                                                        parseFloat(prevYearData[index].attritionPercentage) || 0 : 0;
                                                    const trend = currentAttrition > prevAttrition ? 'up' :
                                                        currentAttrition < prevAttrition ? 'down' : 'same';

                                                    return {
                                                        label: item.month?.split(' ')[0] || '',
                                                        value: currentAttrition,
                                                        secondaryValue: prevAttrition,
                                                        trend: trend
                                                    };
                                                });
                                            } else {
                                                return displayData.map((item: any) => ({
                                                    label: item.departmentName?.substring(0, 10),
                                                    value: item.attritionPercent || 0,
                                                    secondaryValue: undefined
                                                }));
                                            }
                                        })()}
                                        primaryLabel={viewType === "monthly" ? "Current Year" : "Attrition Rate"}
                                        secondaryLabel={viewType === "monthly" ? "Previous Year" : undefined}
                                        yAxisLabel="Attrition %"
                                        showTrendMarkers={viewType === "monthly"}
                                        showComparison={viewType === "monthly"}
                                        height={250}
                                    />
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="ti ti-chart-bar display-4 text-muted mb-3"></i>
                                    <h6 className="mb-2">No Attrition Data Available</h6>
                                    <p className="text-muted">Attrition data will appear here when available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default AttritionReport;